import React, { useState, useRef, useEffect } from "react";
import ReactDatePicker from "react-datepicker";
import { format, isValid, parse, parseISO, setMonth, setYear } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || "");
const isUsDate = (value) => /^\d{2}\/\d{2}\/\d{4}$/.test(value || "");

const parseDateValue = (value) => {
  if (!value) return null;
  if (isIsoDate(value)) {
    const iso = parseISO(value);
    return isValid(iso) ? iso : null;
  }
  if (isUsDate(value)) {
    const us = parse(value, "MM/dd/yyyy", new Date());
    return isValid(us) ? us : null;
  }
  return null;
};

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 30 }, (_, i) => currentYear - 5 + i);

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --ndp-accent: #FF8820;
    --ndp-accent-hover: #e6731a;
    --ndp-accent-light: #fff4ea;
    --ndp-border: #e2e8f0;
    --ndp-text: #0f172a;
    --ndp-text-muted: #94a3b8;
    --ndp-bg: #ffffff;
    --ndp-hover: #f8fafc;
    --ndp-shadow: 0 8px 24px -4px rgba(0,0,0,0.1), 0 2px 8px -2px rgba(0,0,0,0.06);
    --ndp-radius: 10px;
    --ndp-font: 'DM Sans', system-ui, sans-serif;
  }

  .ndp-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;
    font-family: var(--ndp-font);
    max-width: 100%;
    box-sizing: border-box;
  }

  .ndp-calendar-box {
    position: absolute;
    top: calc(100% + 5px);
    right: 0;
    z-index: 99999;
    background: var(--ndp-bg);
    border: 1px solid var(--ndp-border);
    border-radius: var(--ndp-radius);
    width: 220px;
    max-width: calc(100vw - 24px);
    box-sizing: border-box;
    box-shadow: var(--ndp-shadow);
    overflow: hidden;
    animation: ndp-in 0.16s cubic-bezier(0.34, 1.3, 0.64, 1) both;
  }

  @keyframes ndp-in {
    from { opacity: 0; transform: translateY(-5px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .ndp-calendar-box .react-datepicker {
    font-family: var(--ndp-font);
    border: none;
    border-radius: 0;
    box-shadow: none;
    font-size: 12px;
    padding: 0;
    background: var(--ndp-bg);
    width: 100%;
  }
  .ndp-calendar-box .react-datepicker__month-container { width: 100%; }
  .ndp-calendar-box .react-datepicker__triangle { display: none; }
  .ndp-calendar-box .react-datepicker__header {
    background: var(--ndp-bg);
    border-bottom: none;
    padding: 0;
    border-radius: 0;
  }
  .ndp-calendar-box .react-datepicker__day-names {
    display: flex;
    justify-content: space-around;
    padding: 0 8px 2px;
    margin: 0;
  }
  .ndp-calendar-box .react-datepicker__day-name {
    color: var(--ndp-text-muted);
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.03em;
    width: 26px;
    line-height: 20px;
    text-align: center;
    margin: 0;
    text-transform: uppercase;
  }
  .ndp-calendar-box .react-datepicker__month {
    margin: 0;
    padding: 2px 8px 8px;
  }
  .ndp-calendar-box .react-datepicker__week {
    display: flex;
    justify-content: space-around;
  }
  .ndp-calendar-box .react-datepicker__day {
    width: 26px;
    height: 26px;
    line-height: 26px;
    text-align: center;
    font-size: 12px;
    font-weight: 400;
    color: var(--ndp-text);
    border-radius: 6px;
    margin: 1px 0;
    transition: background 0.1s, color 0.1s, transform 0.1s;
  }
  .ndp-calendar-box .react-datepicker__day:hover {
    background: var(--ndp-accent-light);
    color: var(--ndp-accent);
    transform: scale(1.1);
  }
  .ndp-calendar-box .react-datepicker__day--selected {
    background: var(--ndp-accent) !important;
    color: #fff !important;
    font-weight: 600;
    border-radius: 6px;
    box-shadow: 0 2px 6px rgba(255,136,32,0.4);
  }
  .ndp-calendar-box .react-datepicker__day--today:not(.react-datepicker__day--selected) {
    background: var(--ndp-accent-light);
    color: var(--ndp-accent);
    font-weight: 600;
  }
  .ndp-calendar-box .react-datepicker__day--keyboard-selected {
    background: none;
    color: var(--ndp-text);
  }
  .ndp-calendar-box .react-datepicker__day--outside-month {
    color: var(--ndp-text-muted);
  }

  .ndp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 10px 6px;
    border-bottom: 1px solid var(--ndp-border);
  }

  .ndp-month-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ndp-text);
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
    background: none;
    border: none;
    padding: 3px 6px;
    border-radius: 5px;
    font-family: var(--ndp-font);
    transition: background 0.1s;
    margin-left: -6px;
  }
  .ndp-month-label:hover { background: var(--ndp-accent-light); color: var(--ndp-accent); }

  .ndp-caret {
    font-size: 7px;
    color: var(--ndp-text-muted);
    transition: transform 0.2s ease;
  }
  .ndp-caret.open { transform: rotate(180deg); }

  .ndp-nav-group { display: flex; gap: 1px; }

  .ndp-arrow-btn {
    background: none;
    border: 1px solid var(--ndp-border);
    cursor: pointer;
    padding: 2px 10px;
    border-radius: 5px;
    font-size: 18px;
    font-weight: 300;
    color: var(--ndp-text);
    display: flex;
    align-items: center;
    transition: background 0.1s, border-color 0.1s, color 0.1s;
    line-height: 1;
  }
  .ndp-arrow-btn:hover {
    background: var(--ndp-accent-light);
    border-color: var(--ndp-accent);
    color: var(--ndp-accent);
  }

  .ndp-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px 8px;
    border-top: 1px solid var(--ndp-border);
  }
  .ndp-footer-btn {
    background: none;
    border: none;
    color: var(--ndp-accent);
    font-size: 11.5px;
    font-weight: 500;
    cursor: pointer;
    padding: 3px 6px;
    border-radius: 5px;
    font-family: var(--ndp-font);
    transition: background 0.1s;
  }
  .ndp-footer-btn:hover { background: var(--ndp-accent-light); }

  .ndp-selector-scroll {
    max-height: 210px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #e2e8f0 transparent;
  }
  .ndp-selector-scroll::-webkit-scrollbar { width: 3px; }
  .ndp-selector-scroll::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 3px;
  }

  .ndp-year-item { border-bottom: 1px solid var(--ndp-border); }
  .ndp-year-item:last-child { border-bottom: none; }

  .ndp-year-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    font-size: 12px;
    font-weight: 400;
    font-family: var(--ndp-font);
    color: var(--ndp-text);
    cursor: pointer;
    padding: 7px 14px;
    box-sizing: border-box;
    transition: background 0.1s;
  }
  .ndp-year-row:hover { background: var(--ndp-hover); }
  .ndp-year-row.active {
    font-weight: 600;
    color: var(--ndp-accent);
    background: var(--ndp-accent-light);
  }

  .ndp-year-chevron {
    font-size: 9px;
    color: var(--ndp-text-muted);
    transition: transform 0.18s cubic-bezier(0.34,1.2,0.64,1);
  }
  .ndp-year-chevron.open {
    transform: rotate(90deg);
    color: var(--ndp-accent);
  }

  .ndp-months-accordion {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2px;
    padding: 7px 10px 9px;
    background: #fdf8f3;
    border-top: 1px solid var(--ndp-border);
    animation: ndp-slide-down 0.16s cubic-bezier(0.34,1.2,0.64,1) both;
  }

  @keyframes ndp-slide-down {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ndp-mon-btn {
    background: none;
    border: 1px solid transparent;
    font-size: 11px;
    font-weight: 400;
    font-family: var(--ndp-font);
    color: var(--ndp-text);
    cursor: pointer;
    padding: 4px 2px;
    text-align: center;
    border-radius: 6px;
    transition: background 0.1s, color 0.1s, transform 0.1s;
  }
  .ndp-mon-btn:hover {
    background: var(--ndp-accent-light);
    color: var(--ndp-accent);
    transform: scale(1.07);
  }
  .ndp-mon-btn.active {
    background: var(--ndp-accent);
    color: #fff;
    font-weight: 600;
    box-shadow: 0 2px 5px rgba(255,136,32,0.35);
  }
`;

function CalendarHeader({ date, decreaseMonth, increaseMonth, setShowSelector }) {
  return (
    <div className="ndp-header">
      <button className="ndp-month-label" onClick={() => setShowSelector(true)} type="button">
        <span>{format(date, "MMMM yyyy", { locale: enUS })}</span>
        <span className="ndp-caret">▼</span>
      </button>
      <div className="ndp-nav-group">
        <button className="ndp-arrow-btn" onClick={decreaseMonth} type="button">‹</button>
        <button className="ndp-arrow-btn" onClick={increaseMonth} type="button">›</button>
      </div>
    </div>
  );
}

function AccordionSelector({ viewDate, onSelect, onClose }) {
  const [expandedYear, setExpandedYear] = useState(viewDate.getFullYear());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      const active = scrollRef.current.querySelector(".active");
      if (active) active.scrollIntoView({ block: "center" });
    }
  }, []);

  return (
    <>
      <div className="ndp-header">
        <button className="ndp-month-label" onClick={onClose} type="button">
          <span>{format(viewDate, "MMMM yyyy", { locale: enUS })}</span>
          <span className="ndp-caret open">▼</span>
        </button>
      </div>
      <div className="ndp-selector-scroll" ref={scrollRef}>
        {YEARS.map((y) => (
          <div className="ndp-year-item" key={y}>
            <button
              type="button"
              className={`ndp-year-row${viewDate.getFullYear() === y ? " active" : ""}`}
              onClick={() => setExpandedYear((prev) => (prev === y ? null : y))}
            >
              <span>{y}</span>
              <span className={`ndp-year-chevron${expandedYear === y ? " open" : ""}`}>›</span>
            </button>
            {expandedYear === y && (
              <div className="ndp-months-accordion">
                {MONTHS_SHORT.map((m, i) => (
                  <button
                    key={m}
                    type="button"
                    className={`ndp-mon-btn${viewDate.getFullYear() === y && viewDate.getMonth() === i ? " active" : ""}`}
                    onClick={() => onSelect(y, i)}
                  >
                    {m}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

export default function NewDatepicker({
  value = "",
  onChange = () => null,
  disabled = false,
  className = "",
  placeholder = "MM/DD/YYYY",
  minDate = null,
  maxDate = null,
}) {
  const [open, setOpen] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  const wrapperRef = useRef(null);
  const selected = parseDateValue(value);

  useEffect(() => {
    if (value) {
      const d = parseDateValue(value);
      if (d) setViewDate(d);
    }
  }, [value]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setShowSelector(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const closeAll = () => { setOpen(false); setShowSelector(false); };

  const handleDateChange = (date) => {
    onChange(date ? format(date, "yyyy-MM-dd") : "");
    if (date) setViewDate(date);
    closeAll();
  };

  const handleClear = () => { onChange(""); closeAll(); };

  const handleToday = () => {
    const today = new Date();
    onChange(format(today, "yyyy-MM-dd"));
    setViewDate(today);
    closeAll();
  };

  const handleAccordionSelect = (year, monthIndex) => {
    setViewDate(setMonth(setYear(new Date(), year), monthIndex));
    setShowSelector(false);
  };

  return (
    <>
      <style>{styles}</style>
      <div ref={wrapperRef} className={`ndp-wrapper ${className}`}>
        <ReactDatePicker
          selected={selected}
          onChange={(date) => onChange(date ? format(date, "yyyy-MM-dd") : "")}
          locale={enUS}
          dateFormat="MM/dd/yyyy"
          placeholderText={placeholder}
          disabled={disabled}
          showPopperArrow={false}
          popperPlacement="bottom-start"
          wrapperClassName="w-full"
          fixedHeight
          autoComplete="off"
          readOnly
          onInputClick={() => { setOpen(true); setShowSelector(false); }}
          open={false}
        />

        {open && (
          <div className="ndp-calendar-box">
            {showSelector ? (
              <AccordionSelector
                viewDate={viewDate}
                onSelect={handleAccordionSelect}
                onClose={() => setShowSelector(false)}
              />
            ) : (
              <ReactDatePicker
                selected={selected}
                onChange={handleDateChange}
                locale={enUS}
                dateFormat="MM/dd/yyyy"
                inline
                fixedHeight
                disabled={disabled}
                openToDate={viewDate}
                onMonthChange={(d) => setViewDate(d)}
                minDate={minDate}
                maxDate={maxDate}
                renderCustomHeader={(props) => (
                  <CalendarHeader {...props} setShowSelector={setShowSelector} />
                )}
              />
            )}
            <div className="ndp-footer">
              <button className="ndp-footer-btn" onClick={handleClear} type="button">Clear</button>
              <button className="ndp-footer-btn" onClick={handleToday} type="button">Today</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}